import React from 'react'
import { ConfigProvider, Flex, Segmented } from 'antd'
import DomainSettingsTab, {
    IDomainSettingsTab,
    IDomainSettingsTabState,
} from '../abstract-domain-settings-tab/abstract-domain-settings-tab'
import { AiOverview } from './ai-overview'
import { BrandVoiceSettings } from './brand-voice/brand-voice-settings'
import { CuratorSettings } from './curator-settings'
import { PageAnalysisSettings } from './page-analysis-settings'
import { AI_VIEW_LABELS, AiView, DEFAULT_AI_VIEW } from './constants'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { getClassNames } from '../../_utils/classnames'

import './domain-ai-settings.scss'

interface IDomainAiSettings extends IDomainSettingsTab {
    view: AiView
    /** The views the settings rail lists, `'loading'` while their flags are unknown. */
    availableViews: AiView[] | 'loading'
    onViewChange: (view: AiView) => void
}

interface IState extends IDomainSettingsTabState {}

/**
 * The AI section: one feature per view, each laid out as state, then what it
 * did, then its settings, then a way to try it before committing.
 *
 * Which views are offered is the settings registry's call (getAiViews), so the
 * switcher here always matches the rail.
 */
class DomainAiSettings extends DomainSettingsTab<IDomainAiSettings, IState> {
    public render() {
        const { domain, loading } = this.state
        const view = this.resolveView()

        return (
            <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('domain-ai-settings')}>
                <ConfigProvider
                    theme={{
                        components: {
                            Form: {
                                itemMarginBottom: 4,
                                labelColor: 'var(--color-gray-dark)',
                            },
                        },
                    }}
                >
                    <Flex vertical gap={VERTICAL_WELL_GAP}>
                        {!loading && this.availableViews.length > 1 && (
                            <Segmented
                                className={getClassNames('domain-ai-settings-nav')}
                                value={view}
                                onChange={(next) => this.props.onViewChange(next as AiView)}
                                options={this.availableViews.map((key) => ({
                                    value: key,
                                    label: AI_VIEW_LABELS[key],
                                }))}
                            />
                        )}

                        {view === AiView.OVERVIEW && (
                            <AiOverview
                                loading={loading}
                                domain={domain}
                                availableViews={this.availableViews}
                                onViewChange={this.props.onViewChange}
                            />
                        )}

                        {view === AiView.BRAND_VOICE && <BrandVoiceSettings loading={loading} domain={domain} />}

                        {view === AiView.CURATOR && <CuratorSettings loading={loading} domain={domain} />}

                        {view === AiView.PAGE_ANALYSIS && <PageAnalysisSettings loading={loading} domain={domain} />}
                    </Flex>
                </ConfigProvider>
            </Flex>
        )
    }

    /**
     * A view this user cannot reach falls back rather than rendering nothing,
     * so a link to an internal-only view does not dead-end an external user.
     */
    protected resolveView(): AiView {
        // Falling back before the flags are known would bounce a deep link to
        // the overview and then jump once they arrive.
        if (this.props.availableViews === 'loading') {
            return this.props.view
        }

        return this.availableViews.includes(this.props.view) ? this.props.view : DEFAULT_AI_VIEW
    }

    protected get availableViews(): AiView[] {
        const { availableViews } = this.props
        return availableViews === 'loading' ? [AiView.OVERVIEW] : availableViews
    }
}

export default DomainAiSettings
